import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter, useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Connector, useAccount, useDisconnect } from 'wagmi';

import { useWalletAuth } from '@/lib/auth/runWalletLogin.client';
import { trackAnalyticsEvent } from '@/lib/utils/trackAnalyticsEvent';
import {
  useUpdateGlobalData,
  useGlobalData
} from '@/providers/GlobalDataProvider';
import { T_User } from '@/ts/types/T_User';

interface I_UseWalletConnectionState {
  isConnected: boolean;
  address: string | undefined;
  isLoading: boolean;
  isFullyLoggedIn: boolean;
  isUserData: boolean;
  connector: Connector | undefined;
}

interface I_UseWalletConnectionActions {
  openWalletConnect: () => void;
  closeWalletConnect: () => void;
  openWelcome: () => void;
  closeWelcome: () => void;
  openAccount: () => void;
  closeAccount: () => void;
  disconnectAsync: () => Promise<void>;
  handleWalletPick: (walletId: string) => void;
  setPendingWallet: (walletId: string, connectorName?: string) => void;
}

export function useWalletConnection(): I_UseWalletConnectionState &
  I_UseWalletConnectionActions {
  const { isConnected, address, isConnecting, connector } = useAccount();
  const { disconnectAsync } = useDisconnect();
  const updateGlobalData = useUpdateGlobalData();
  const router = useRouter();
  const params = useParams();

  // Use proper internationalization for translations
  const t = useTranslations('editProfile.messages');

  const { run } = useWalletAuth(
    (v: { user: unknown }) =>
      updateGlobalData({ user: v.user as T_User | null }),
    t
  );
  const user = useGlobalData('user');
  const isWalletConnectOpen = useGlobalData('isWalletConnectOpen') || false;
  const isAuthenticating = useGlobalData('isAuthenticating') || false;
  const [pendingWalletId, setPendingWalletId] = useState<string | null>(null);
  const [pendingConnectorName, setPendingConnectorName] = useState<
    string | null
  >(null);
  const [mounted, setMounted] = useState(false);
  const [isRewardSubdomain, setIsRewardSubdomain] = useState(false);

  const pendingWelcomeRef = useRef(false);

  // Track when the component has mounted on the client
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const normalizedSubdomain = (
      process.env.NEXT_PUBLIC_SUBDOMAIN_NAME || 'reward'
    ).toLowerCase();
    const hostname = window.location.hostname.toLowerCase();

    const matchesHostname =
      normalizedSubdomain.length > 0 &&
      hostname.startsWith(`${normalizedSubdomain}.`);

    setIsRewardSubdomain(matchesHostname);
  }, []);

  useEffect(() => {
    if (!mounted || typeof window === 'undefined' || !user) return;
    if (isRewardSubdomain) return;
    if (
      !pendingWelcomeRef.current &&
      sessionStorage.getItem('pending_welcome') !== 'true'
    )
      return;

    const currentUrlLocale = ((params.locale as string) || 'en').toLowerCase();
    const userLocale = (user.language || currentUrlLocale).toLowerCase();

    if (currentUrlLocale === userLocale) {
      pendingWelcomeRef.current = false;
      sessionStorage.removeItem('pending_welcome');

      setTimeout(() => {
        updateGlobalData({ isWelcomeOpen: true });
      }, 300);
    }
  }, [mounted, user, params.locale, isRewardSubdomain, updateGlobalData]);

  // Memoize loading state calculation to prevent unnecessary re-renders
  const isLoading = useMemo(
    () => !mounted || isConnecting,
    [mounted, isConnecting]
  );

  // User is only considered logged in when both wallet is connected AND backend authentication is successful
  const isFullyLoggedIn = useMemo(
    () => isConnected && !!user && !!address,
    [isConnected, user, address]
  );
  const isUserData = useMemo(() => !!user, [user]);
  const openWalletConnect = useCallback(async () => {
    if (!isFullyLoggedIn) {
      await disconnectAsync();
    }
    updateGlobalData({ isWalletConnectOpen: true });
  }, [disconnectAsync, isFullyLoggedIn, updateGlobalData]);

  const closeWalletConnect = useCallback(() => {
    updateGlobalData({ isWalletConnectOpen: false });
  }, [updateGlobalData]);

  const openWelcome = useCallback(() => {
    if (isRewardSubdomain) {
      return;
    }

    if (isConnected && !!user && !!address) {
      updateGlobalData({ isWelcomeOpen: true });
    } else {
      console.log('Cannot open welcome dialog - user not fully logged in');
    }
  }, [isConnected, user, address, updateGlobalData, isRewardSubdomain]);

  // Close welcome dialog
  const closeWelcome = useCallback(() => {
    updateGlobalData({ isWelcomeOpen: false });
  }, [updateGlobalData]);

  // Open AccountSheet sheet
  const openAccount = useCallback(() => {
    updateGlobalData({ isAccountOpen: true });
  }, [updateGlobalData]);

  // Close AccountSheet sheet
  const closeAccount = useCallback(() => {
    updateGlobalData({ isAccountOpen: false });
  }, [updateGlobalData]);

  //TODO change or delete
  const handleWalletPick = useCallback(() => {
    // Wallet authentication is now handled in WalletConnectDialog
  }, []);

  // Function to set pending wallet ID and connector name (called from WalletConnectDialog)
  const setPendingWallet = useCallback(
    (walletId: string, connectorName?: string) => {
      // Don't set pending wallet if already authenticating
      if (isAuthenticating) {
        console.log(
          'Authentication already in progress, ignoring new wallet selection'
        );
        return;
      }
      setPendingWalletId(walletId);
      setPendingConnectorName(connectorName || null);
    },
    [isAuthenticating]
  );

  // Effect to handle successful connection
  useEffect(() => {
    if (!mounted) return; // Don't run until mounted on client

    if (isConnected && address && pendingWalletId && !isAuthenticating) {
      const authenticateWallet = async () => {
        updateGlobalData({ isAuthenticating: true });

        try {
          if (pendingConnectorName) {
            // Double-check that we're still connected before authenticating
            if (isConnected && address) {
              // 1. Run auth - if this throws, we jump to catch
              await run({
                onConnected: () => {
                  if (!isRewardSubdomain) {
                    sessionStorage.setItem('pending_welcome', 'true');
                    pendingWelcomeRef.current = true;
                  }
                }
              });

              // 2. Only if run() succeeds do we proceed
              if (isWalletConnectOpen) {
                updateGlobalData({ isWalletConnectOpen: false });
              }
              console.log('Wallet authentication successful');

              trackAnalyticsEvent('wallet_connected', {
                wallet_id: pendingWalletId,
                connector_name: pendingConnectorName
              });

              localStorage.setItem(
                'wallet-connect-confirmation-approved',
                'true'
              );

              if (isRewardSubdomain) {
                router.refresh();
              }
            } else {
              console.warn('Wallet connection lost before authentication');
            }
          }
        } catch (authError) {
          console.error('Wallet authentication failed:', authError);
          // Clear user from global state since authentication failed
          updateGlobalData({ user: null });

          // If authentication fails, disconnect the wallet explicitly
          try {
            await disconnectAsync();
            console.log('Wallet disconnected due to authentication failure');
          } catch (disconnectError) {
            console.error(
              'Failed to disconnect wallet after auth failure:',
              disconnectError
            );
          }
        } finally {
          // Always reset authenticating state
          updateGlobalData({ isAuthenticating: false });
          setPendingWalletId(null);
          setPendingConnectorName(null);
        }
      };

      authenticateWallet();
    }
  }, [
    mounted,
    isConnected,
    address,
    pendingWalletId,
    pendingConnectorName,
    isAuthenticating,
    isConnecting,
    disconnectAsync,
    updateGlobalData,
    run,
    isWalletConnectOpen,
    isRewardSubdomain,
    router
  ]);

  // Return state and actions directly - memoization was causing dependency issues
  return {
    // State
    isConnected,
    address,
    isLoading,
    isFullyLoggedIn,
    connector,
    isUserData,

    // Actions
    openWalletConnect,
    closeWalletConnect,
    openWelcome,
    closeWelcome,
    openAccount,
    closeAccount,
    handleWalletPick,
    setPendingWallet,
    disconnectAsync
  };
}
